/**
 * Inoriza catalog: native Shopify filtering with progressive enhancement.
 * No product is filtered, counted, sorted or hidden client-side.
 * The server renders every filtered page via the Section Rendering API.
 * Native GET forms and pagination remain functional without JavaScript.
 */
(() => {
  'use strict';
  if (customElements.get('ir-catalog')) return;
  const mobile = () => window.matchMedia('(max-width: 989px)').matches;
  class IRCatalog extends HTMLElement {
    connectedCallback() {
      if (!this.querySelector('[data-ir-filter-form]')) {
        this.readyFrame = requestAnimationFrame(() => {
          if (this.isConnected && this.querySelector('[data-ir-filter-form]')) this.connectedCallback();
        });
        return;
      }
      this.events?.abort();
      this.events = new AbortController();
      const opts = { signal: this.events.signal };
      this.initialPath = location.pathname;
      this.sequence = 0;
      this.enhance();
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-ir-filter-form]')) return;
        event.preventDefault();
        this.apply(true);
      }, opts);
            this.addEventListener('change', event => {
        const field = event.target;

        if (!(field instanceof HTMLInputElement ||
              field instanceof HTMLSelectElement)) return;

        if (field.name === 'sort_by') return this.apply(true);
        if (!field.name.startsWith('filter.')) return;

        field.setCustomValidity('');

        if (
          field instanceof HTMLInputElement &&
          field.type === 'checkbox' &&
          field.checked
        ) {
          const autoStock =
            field.name === 'filter.p.vendor' ||
            field.name === 'filter.p.product_type' ||
            field.name.startsWith('filter.v.option.');

          const availability = [...this.querySelectorAll(
            '[data-ir-filter-form] input[type="checkbox"][name="filter.v.availability"]'
          )];

          const inStock = availability.find(
            input => input.value === '1'
          );

          // Marca, tipo o talle: seleccionar stock y quitar agotados.
          if (autoStock && inStock) {
            availability.forEach(input => {
              input.checked = input === inStock;
            });

            // Incluso con cero coincidencias, enviar el filtro.
            inStock.disabled = false;

            inStock.closest('.ir-filter-choice')
              ?.classList.remove('ir-filter-choice--disabled');
          }

          // Evitar seleccionar a la vez "En existencia" y "Agotado".
          if (field.name === 'filter.v.availability') {
            availability.forEach(input => {
              if (input !== field) input.checked = false;
            });
          }
        }

        // Celular: marcar ahora y aplicar con "Aplicar filtros".
        if (mobile()) return;

        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => this.apply(false), 180);
      }, opts);
      this.addEventListener('input', event => {
        if (event.target.matches('[data-ir-price-min],[data-ir-price-max]')) {
          this.querySelectorAll('[data-ir-price-min],[data-ir-price-max]').forEach(input => input.setCustomValidity(''));
        }
      }, opts);
      this.addEventListener('click', event => this.onClick(event), opts);
      this.addEventListener('keydown', event => this.onKeydown(event), opts);
      window.addEventListener('popstate', () => {
        clearTimeout(this.debounce);
        if (location.pathname !== this.initialPath) return location.reload();
        this.load(new URL(location.href), { history: false, scroll: true });
      }, opts);
      window.addEventListener('resize', () => {
        if (!mobile() && this.dataset.irFiltersOpen === 'true') this.closeFilters(false);
      }, opts);
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.readyFrame);
      this.events?.abort();
      this.request?.abort();
      clearTimeout(this.debounce);
      this.unlock();
    }
    enhance() {
      this.dataset.irEnhanced = 'true';
      this.dataset.irFiltersOpen = 'false';
      this.querySelector('[data-ir-open-filters]')?.removeAttribute('hidden');
    }
    onClick(event) {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-ir-open-filters]')) {
        this.openFilters();
        return;
      }
      if (event.target.closest('[data-ir-close-filters]')) {
        this.closeFilters();
        return;
      }
      const link = event.target.closest('a[data-ir-filter-link],a[data-ir-page]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      event.preventDefault();
      this.preservePreview(url);
      this.load(url, { history: true, scroll: link.hasAttribute('data-ir-page') });
    }
    preservePreview(url) {
      const current = new URL(location.href);
      ['preview_theme_id', '_ab', '_fd', 'pb'].forEach(key => {
        if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
      });
      url.searchParams.delete('section_id');
      return url;
    }
    formURL() {
      const form = this.querySelector('[data-ir-filter-form]');
      if (!form) return null;
      const min = form.querySelector('[data-ir-price-min]');
      const max = form.querySelector('[data-ir-price-max]');
      min?.setCustomValidity('');
      max?.setCustomValidity('');
      if (min?.value !== '' && max?.value !== '' && min && max && Number(min.value) > Number(max.value)) {
        max.setCustomValidity('El precio m\u00e1ximo debe ser mayor o igual al m\u00ednimo.');
      }
      if (!form.reportValidity()) return null;
      const url = new URL(form.action, location.href);
      const current = new URL(location.href);
      const excluded = new Set(['page', 'sort_by', 'q', 'type', 'options[prefix]', 'section_id']);
      url.search = '';
      for (const [key, value] of current.searchParams) {
        if (!excluded.has(key) && !key.startsWith('filter.')) url.searchParams.append(key, value);
      }
      for (const [key, value] of new FormData(form)) {
        if (typeof value === 'string' && value.trim() !== '') url.searchParams.append(key, value);
      }
      return url;
    }
    apply(scroll) {
      clearTimeout(this.debounce);
      const url = this.formURL();
      if (url) this.load(url, { history: true, scroll });
    }
    async load(url, { history: updateHistory = true, scroll = false } = {}) {
      clearTimeout(this.debounce);
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const sequence = ++this.sequence;
      const focusId = this.contains(document.activeElement) ? document.activeElement.id : '';
      const openGroups = new Set([...this.querySelectorAll('.ir-filter-group[open]')].map(group => group.id));
      const wasMobileOpen = this.dataset.irFiltersOpen === 'true';
      this.setAttribute('aria-busy', 'true');
      this.dataset.irLoading = 'true';
      const fetchURL = new URL(url);
      fetchURL.searchParams.set('section_id', this.dataset.sectionId);
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; request.abort(); }, 15000);
      try {
        const response = await fetch(fetchURL.toString(), {
          signal: request.signal,
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) throw new Error(`Section response ${response.status}`);
        const html = await response.text();
        if (sequence !== this.sequence) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.getElementById(this.id);
        if (!fresh) throw new Error('Catalog section missing');
        this.closeFilters(false);
        this.innerHTML = fresh.innerHTML;
        this.dataset.irHasFilters = fresh.dataset.irHasFilters;
        this.enhance();
        for (const group of this.querySelectorAll('.ir-filter-group')) group.open = openGroups.has(group.id);
        if (updateHistory && url.toString() !== location.href) {
          window.history.pushState({ irCatalog: this.id }, '', url.toString());
        }
        const status = this.querySelector('[data-ir-results-status]');
        const message = status?.textContent.trim();
        if (status) {
          status.textContent = '';
          requestAnimationFrame(() => { if (status.isConnected) status.textContent = message; });
        }
        const results = this.querySelector('.ir-catalog-results');
        const focus = focusId ? document.getElementById(focusId) : null;
        if (wasMobileOpen) {
          this.querySelector('[data-ir-open-filters]')?.focus({ preventScroll: true });
        } else if (focus && this.contains(focus)) {
          focus.focus({ preventScroll: true });
        } else if (scroll) {
          results?.focus({ preventScroll: true });
        }
        if (scroll || wasMobileOpen) results?.scrollIntoView({ block: 'start', behavior: 'auto' });
        this.dispatchEvent(new CustomEvent('ir:catalog:updated', { bubbles: true, detail: { url: url.toString() } }));
      } catch (error) {
        // Aborted superseded requests must never redirect over a newer filter state.
        if (sequence !== this.sequence || !this.isConnected) return;
        if (error.name === 'AbortError' && !timedOut) return;
        this.closeFilters(false);
        window.location.assign(url.toString());
      } finally {
        clearTimeout(timer);
        if (sequence === this.sequence) {
          this.removeAttribute('aria-busy');
          delete this.dataset.irLoading;
        }
      }
    }
    openFilters() {
      this.returnFocus = document.activeElement;
      this.dataset.irFiltersOpen = 'true';
      this.querySelector('[data-ir-open-filters]')?.setAttribute('aria-expanded', 'true');
      const panel = this.querySelector('.ir-filter-panel');
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
      this.querySelector('.ir-filter-backdrop')?.removeAttribute('hidden');
      if (this.previousOverflow === undefined) this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      panel?.querySelector('[data-ir-close-filters]')?.focus();
    }
    unlock() {
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
    }
    closeFilters(restoreFocus = true) {
      this.dataset.irFiltersOpen = 'false';
      this.querySelector('[data-ir-open-filters]')?.setAttribute('aria-expanded', 'false');
      const panel = this.querySelector('.ir-filter-panel');
      panel?.removeAttribute('role');
      panel?.removeAttribute('aria-modal');
      this.querySelector('.ir-filter-backdrop')?.setAttribute('hidden', '');
      this.unlock();
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
    onKeydown(event) {
      if (!mobile() || this.dataset.irFiltersOpen !== 'true') return;
      if (event.key === 'Escape') { event.preventDefault(); this.closeFilters(); return; }
      if (event.key !== 'Tab') return;
      const focusable = [...this.querySelector('.ir-filter-panel').querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select,summary')].filter(el => el.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }
  customElements.define('ir-catalog', IRCatalog);
})();
